
import React, { useState, useEffect, useRef } from 'react';
import { dbService } from '../services/dbService.ts';
import { aiService } from '../services/aiService.ts';
import { UserRole, SubscriptionTier, ProfessionalProfile } from '../types.ts';

const CareerStrategistView = ({ user, onChangeTab }: any) => {
  const isProfessional = user.role === UserRole.PROFESSIONAL;
  const profile = isProfessional 
    ? dbService.getProfessionalProfile(user.id) 
    : dbService.getStudentProfile(user.id);
    
  const userTier = user.tier || SubscriptionTier.BASIC;
  const isLocked = !isProfessional && userTier !== SubscriptionTier.ADVANCED;

  const [aspirations, setAspirations] = useState("");
  const [strategy, setStrategy] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadStep, setLoadStep] = useState(0);
  
  const resultsRef = useRef<HTMLDivElement>(null);
  const professionals = dbService.getAllProfessionalProfiles();

  const loadMessages = [
    "Analyzing Industry Trajectories...",
    "Benchmarking High-Performer Records...",
    "Scanning Internal Professional Directory...",
    "Calculating Synergy Overlap...",
    "Architecting Strategic Roadmap..."
  ];

  useEffect(() => {
    let interval: number;
    if (isLoading) {
      setLoadStep(0);
      interval = window.setInterval(() => {
        setLoadStep(prev => (prev < loadMessages.length - 1 ? prev + 1 : prev));
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  // Mobile Auto-Scroll Effect
  useEffect(() => {
    if (strategy && resultsRef.current && window.innerWidth < 768) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [strategy]);

  const handleGenerateStrategy = async () => {
    if (!aspirations) {
      alert("Please provide some goals or aspirations first!");
      return;
    }
    setIsLoading(true);
    setStrategy(null);
    try {
      const result = await aiService.generateCareerStrategy(profile, aspirations);
      setStrategy(result);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const findLocalMatches = (keywords: string[] = []) => {
    if (!keywords || keywords.length === 0) return [];
    return professionals.filter(p => 
      keywords.some(k => 
        p.title.toLowerCase().includes(k.toLowerCase()) || 
        p.industry.toLowerCase().includes(k.toLowerCase()) ||
        p.company.toLowerCase().includes(k.toLowerCase())
      )
    ).slice(0, 2);
  };

  const handleInitiateSync = (profId: string) => {
    // Navigate to professionals tab to initiate a connection
    onChangeTab('professionals');
    // In a full implementation, we'd pass state to open this specific prof's modal
  };

  return (
    <div className="p-4 md:p-12 lg:p-20 bg-[#f8fafc] dark:bg-slate-950 min-h-screen transition-colors duration-500 overflow-y-auto font-sans relative">
        {/* Architectural Grid Background */}
        <div className="absolute inset-0 opacity-[0.05] dark:opacity-[0.1] pointer-events-none" 
             style={{ 
               backgroundImage: `linear-gradient(#4f46e5 1px, transparent 1px), linear-gradient(90deg, #4f46e5 1px, transparent 1px)`, 
               backgroundSize: '50px 50px' 
             }}>
        </div>

        <header className="mb-16 relative z-10 animate-in slide-in-from-bottom-4 duration-700">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-full mb-6 shadow-sm">
             <div className="w-2 h-2 bg-indigo-600 rounded-full animate-pulse"></div>
             <span className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-600 dark:text-indigo-400">Career Architect v4.2</span>
          </div>
          <h1 className="text-5xl md:text-8xl font-black text-slate-900 dark:text-white tracking-tighter leading-none mb-6">Strategy <span className="text-indigo-600">Forge.</span></h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium max-w-2xl text-lg md:text-xl">Precise benchmarking from high-performer trajectories, synchronized with our professional network.</p>
        </header>

        {isLocked ? (
          <div className="relative z-10 bg-white dark:bg-slate-900 p-12 md:p-20 rounded-[3rem] md:rounded-[5rem] border border-slate-200 dark:border-slate-800 shadow-4xl text-center flex flex-col items-center animate-in zoom-in-95 duration-500">
             <div className="w-24 h-24 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-[2.5rem] flex items-center justify-center text-4xl mb-10 shadow-inner">
                <i className="fas fa-lock"></i>
             </div>
             <h2 className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter mb-4">Strategist Vault.</h2>
             <p className="text-slate-500 dark:text-slate-400 text-lg font-medium max-w-md mb-12 leading-relaxed">
               The Career Strategist uses industry benchmarking and directory linking reserved for <span className="text-indigo-600 font-black">Advanced</span> members.
             </p>
             <button 
               onClick={() => onChangeTab('membership')}
               className="px-12 py-5 bg-slate-900 dark:bg-indigo-600 text-white rounded-3xl font-black text-lg hover:bg-slate-800 transition shadow-2xl active:scale-95"
             >
               Upgrade to Advanced
             </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 relative z-10">
            {/* Input Sidebar */}
            <div className="lg:col-span-4 space-y-8">
               <div className="bg-white dark:bg-slate-900 p-8 md:p-10 rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-2xl sticky top-8">
                  <div className="flex items-center gap-3 mb-8">
                     <i className="fas fa-terminal text-indigo-600 text-lg"></i>
                     <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">Input Parameters</h3>
                  </div>
                  <textarea 
                    value={aspirations}
                    onChange={e => setAspirations(e.target.value)}
                    placeholder="e.g. Pivot from Backend to Quant Trading at a Tier-1 firm..."
                    className="w-full p-6 rounded-[2rem] bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-indigo-600 dark:focus:border-indigo-400 outline-none font-bold text-slate-900 dark:text-white h-48 transition-all shadow-inner text-sm resize-none mb-6"
                  />
                  <button 
                    onClick={handleGenerateStrategy}
                    disabled={isLoading}
                    className="w-full py-6 bg-indigo-600 text-white rounded-2xl font-black text-[11px] uppercase tracking-widest hover:bg-indigo-500 transition shadow-3xl active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3"
                  >
                    {isLoading ? <i className="fas fa-circle-notch animate-spin"></i> : <i className="fas fa-bolt text-sm"></i>}
                    {isLoading ? 'Synthesizing Roadmap...' : 'Generate Blueprint'}
                  </button>

                  {strategy && (
                    <div className="mt-10 pt-8 border-t border-slate-100 dark:border-slate-800 animate-in fade-in duration-1000">
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Modeled After</p>
                       <div className="space-y-2">
                          {strategy.industryBenchmarks?.map((b: string, i: number) => (
                             <div key={i} className="flex items-center gap-3 px-4 py-3 bg-slate-50 dark:bg-slate-800 rounded-xl text-[10px] font-bold text-slate-600 dark:text-slate-400 border border-slate-100 dark:border-slate-700">
                                <i className="fas fa-check-circle text-indigo-600"></i>
                                {b}
                             </div>
                          ))}
                       </div>
                    </div>
                  )}
               </div>
            </div>

            {/* Main Roadmap Area */}
            <div className="lg:col-span-8" ref={resultsRef}>
               {!strategy && !isLoading ? (
                  <div className="h-[600px] bg-white/50 dark:bg-slate-900/30 border-4 border-dashed border-slate-200 dark:border-slate-800 rounded-[4rem] flex flex-col items-center justify-center p-20 text-center animate-in fade-in duration-700">
                     <div className="w-24 h-24 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center text-4xl text-slate-300 dark:text-slate-700 mb-8">
                        <i className="fas fa-drafting-compass"></i>
                     </div>
                     <h3 className="text-2xl font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest mb-4">Systems Idle.</h3>
                     <p className="text-slate-400 dark:text-slate-500 font-medium max-w-sm">Enter your future aspiration to initiate the benchmarking engine.</p>
                  </div>
               ) : isLoading ? (
                  <div className="h-[600px] bg-white dark:bg-slate-900 rounded-[4rem] border border-slate-200 dark:border-slate-800 shadow-4xl flex flex-col items-center justify-center p-20 relative overflow-hidden">
                     <div className="absolute inset-0 opacity-[0.1] bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
                     <div className="w-24 h-24 relative mb-12">
                        <div className="absolute inset-0 border-4 border-indigo-100 dark:border-slate-800 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                           <i className="fas fa-brain text-2xl text-indigo-600 animate-pulse"></i>
                        </div>
                     </div>
                     <p className="text-[10px] font-black uppercase text-indigo-600 dark:text-indigo-400 tracking-[0.5em] mb-4 animate-pulse">Sage Thinking Protocol</p>
                     <p className="text-xl font-bold text-slate-400 dark:text-slate-500 h-6 text-center">{loadMessages[loadStep]}</p>
                  </div>
               ) : (
                  <div className="space-y-16 pb-32 animate-in fade-in duration-1000">
                     {[strategy.roadmap?.phase1ShortTerm, strategy.roadmap?.phase2MidTerm, strategy.roadmap?.phase3LongTerm].map((phase, i) => {
                        if (!phase) return null;
                        const matchingPros = findLocalMatches(phase.searchKeywords);
                        
                        return (
                          <section key={i} className="relative group">
                             {/* Technical Connector Line */}
                             {i < 2 && (
                               <div className="absolute left-8 top-full h-16 w-1 border-l-2 border-dashed border-slate-200 dark:border-slate-800 z-0"></div>
                             )}
                             
                             <div className="bg-white dark:bg-slate-900 rounded-[3.5rem] md:rounded-[4.5rem] border border-slate-200 dark:border-slate-800 shadow-3xl overflow-hidden relative z-10 transition-all hover:border-indigo-600/20">
                                <header className="p-8 md:p-12 border-b border-slate-100 dark:border-slate-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-slate-50/50 dark:bg-slate-950/20">
                                   <div className="flex items-center gap-6">
                                      <div className="w-16 h-16 bg-slate-900 dark:bg-indigo-600 text-white rounded-2xl flex items-center justify-center text-xl font-black shadow-xl">
                                         0{i+1}
                                      </div>
                                      <div>
                                         <span className="text-[9px] font-black uppercase tracking-widest text-indigo-600 dark:text-indigo-400 mb-1 block">{phase.timeline} Phase</span>
                                         <h3 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tighter leading-none">{phase.title}</h3>
                                      </div>
                                   </div>
                                </header>

                                <div className="p-8 md:p-12 grid grid-cols-1 xl:grid-cols-2 gap-12">
                                   {/* Tasks & Milestones */}
                                   <div className="space-y-8">
                                      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-3">
                                         <i className="fas fa-list-check text-indigo-600"></i> Milestone Logic
                                      </h4>
                                      <div className="space-y-4">
                                         {phase.tasks.map((task: any, j: number) => (
                                            <div key={j} className="p-5 bg-slate-50 dark:bg-slate-800/40 rounded-3xl border border-slate-100 dark:border-slate-700/50 group/task hover:bg-white dark:hover:bg-slate-800 transition-all">
                                               <p className="text-sm font-bold text-slate-900 dark:text-white mb-3 leading-snug">{task.description}</p>
                                               {task.recruitingTimeline && (
                                                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-600 text-white rounded-lg text-[9px] font-black uppercase tracking-widest shadow-md">
                                                     <i className="fas fa-calendar-alt"></i> Recruiting: {task.recruitingTimeline}
                                                  </div>
                                               )}
                                            </div>
                                         ))}
                                      </div>
                                   </div>

                                   {/* Knowledge & Networking */}
                                   <div className="space-y-10">
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                         <div className="space-y-4">
                                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Certs & Credentials</h4>
                                            <div className="flex flex-wrap gap-2">
                                               {phase.certifications?.map((cert: string, j: number) => (
                                                  <div key={j} className="px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-xl text-[9px] font-black uppercase tracking-widest border border-indigo-100 dark:border-indigo-800/30">
                                                     {cert}
                                                  </div>
                                               ))}
                                            </div>
                                         </div>
                                         <div className="space-y-4">
                                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Recommended Training</h4>
                                            <div className="space-y-2">
                                               {phase.recommendedTraining?.map((t: string, j: number) => (
                                                  <div key={j} className="flex items-center gap-2 text-[10px] font-bold text-slate-600 dark:text-slate-400">
                                                     <i className="fas fa-graduation-cap text-indigo-600 text-[8px]"></i> {t}
                                                  </div>
                                               ))}
                                            </div>
                                         </div>
                                      </div>

                                      {/* DIRECTORY LINKING SECTION */}
                                      <div className="mt-8 pt-8 border-t border-slate-100 dark:border-slate-800">
                                         <div className="flex items-center justify-between mb-6">
                                            <h4 className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest flex items-center gap-3">
                                               <i className="fas fa-users-viewfinder"></i> Verified Contacts
                                            </h4>
                                            <span className="text-[8px] font-black text-emerald-600 uppercase bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-md">Live Sync</span>
                                         </div>
                                         
                                         {matchingPros.length > 0 ? (
                                            <div className="space-y-3">
                                               {matchingPros.map((p) => (
                                                  <div key={p.userId} className="p-5 bg-indigo-50/50 dark:bg-indigo-900/10 rounded-2xl border border-indigo-100 dark:border-indigo-800/50 flex items-center justify-between group/pro hover:bg-white dark:hover:bg-indigo-900/20 transition-all shadow-sm">
                                                     <div className="flex items-center gap-4">
                                                        <div className="w-12 h-12 bg-indigo-600 text-white rounded-xl flex items-center justify-center font-black text-lg shadow-lg">
                                                           {p.fullName[0]}
                                                        </div>
                                                        <div>
                                                           <p className="text-sm font-black text-slate-900 dark:text-white">{p.fullName}</p>
                                                           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{p.title} • {p.company}</p>
                                                        </div>
                                                     </div>
                                                     <button 
                                                       onClick={() => handleInitiateSync(p.userId)}
                                                       className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-500 transition-all flex items-center gap-2"
                                                     >
                                                        <i className="fas fa-paper-plane text-[8px]"></i>
                                                        Connect
                                                     </button>
                                                  </div>
                                               ))}
                                            </div>
                                         ) : (
                                            <div className="p-8 bg-slate-50 dark:bg-slate-800/20 rounded-3xl border border-dashed border-slate-200 dark:border-slate-800 text-center">
                                               <p className="text-[10px] font-bold text-slate-400 mb-2 italic">No immediate matches for this specific phase in the directory.</p>
                                               <button onClick={() => onChangeTab('professionals')} className="text-[9px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest hover:underline flex items-center justify-center gap-2 mx-auto">
                                                  <i className="fas fa-search-plus"></i> Expand Search
                                               </button>
                                            </div>
                                         )}
                                      </div>
                                   </div>
                                </div>
                             </div>
                          </section>
                        );
                     })}
                  </div>
               )}
            </div>
          </div>
        )}
    </div>
  );
};

export default CareerStrategistView;
